/**
 * Story 061 — Add-component modal (`/components?new=1`).
 *
 * Required: Park (siteId scaffold; not body-serialised) + Turbine
 * (assetId) + Component Model (assetComponentModelId). Save disabled
 * until all three are picked.
 *
 * Cascade chain:
 *   Park picked → Turbine picker enabled (fires
 *   `listTurbines?site=<id>&pageSize=200`).
 *   Turbine picked → Component Model picker enabled (fires the lean
 *   `listAssetComponentModels?lean=true`). The dropdown groups
 *   "Frequently used" (tenant) and "All component models" (global).
 *
 * Submit body shape (AC16):
 *   { assetId, assetComponentModelId, serialNumber?, manufactureDate?,
 *     installedAt?, lastMaintenanceAt?, status?, externalIds? }
 * — NO id, NO componentCode, NO audit fields (BE-assigned).
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useCanEditResourceFor } from "@aeriosiq/ui";
import { useToast } from "../../account/ToastContext.js";
import {
  useCreateComponentMutation,
  type ComponentExternalId,
  type CreateComponentBody,
} from "../queries/index.js";
import { ExternalIdsField } from "../../sites/shared/ExternalIdsField.js";
import {
  SiteAutocomplete,
  type SiteOption,
} from "../../turbines/shared/SiteAutocomplete.js";
import {
  TurbineAutocomplete,
  type TurbineOption,
} from "../shared/TurbineAutocomplete.js";
import { ComponentModelAutocomplete } from "../shared/ComponentModelAutocomplete.js";
import { useTurbinesListQuery } from "../../turbines/queries/index.js";
import { turbinesToOptions } from "../shared/TurbineAutocomplete.js";
import { useListComponentModelsLeanQuery } from "../../component-models/queries/hooks.js";

const STATUS_OPTIONS = [
  "ACTIVE",
  "SPARE",
  "RETIRED",
  "IN_TRANSIT",
  "IN_MAINTENANCE",
  "DECOMMISSIONED",
  "UNDEFINED",
] as const;

interface AddComponentFormValues {
  siteId: string | null;
  assetId: string | null;
  assetComponentModelId: string | null;
  serialNumber: string;
  manufactureDate: string;
  installedAt: string;
  lastMaintenanceAt: string;
  status: string | null;
  externalIds: ComponentExternalId[];
}

const EMPTY: AddComponentFormValues = {
  siteId: null,
  assetId: null,
  assetComponentModelId: null,
  serialNumber: "",
  manufactureDate: "",
  installedAt: "",
  lastMaintenanceAt: "",
  status: null,
  externalIds: [],
};

function dropEmptyExternalIds(
  arr: ComponentExternalId[],
): ComponentExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildCreateBody(v: AddComponentFormValues): CreateComponentBody {
  const body: CreateComponentBody = {
    assetId: v.assetId ?? "",
    assetComponentModelId: v.assetComponentModelId ?? "",
  };
  if (v.serialNumber.trim().length > 0) {
    body.serialNumber = v.serialNumber.trim();
  }
  if (v.manufactureDate.trim().length > 0) {
    body.manufactureDate = v.manufactureDate.trim();
  }
  if (v.installedAt.trim().length > 0) {
    body.installedAt = v.installedAt.trim();
  }
  if (v.lastMaintenanceAt.trim().length > 0) {
    body.lastMaintenanceAt = v.lastMaintenanceAt.trim();
  }
  if (v.status !== null && v.status.trim().length > 0) {
    body.status = v.status.trim();
  }
  const ids = dropEmptyExternalIds(v.externalIds);
  if (ids.length > 0) body.externalIds = ids;
  return body;
}

export interface AddComponentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Active sites for the Park Autocomplete. */
  siteOptions: readonly SiteOption[];
}

export function AddComponentDialog({
  open,
  onClose,
  siteOptions,
}: AddComponentDialogProps): ReactElement {
  const { toast } = useToast();
  const create = useCreateComponentMutation();
  // SB2-CC-04 — defence-in-depth banner.
  const canCreate = useCanEditResourceFor("component.create_tenant");
  const { control, register, handleSubmit, reset, watch, setValue, formState } =
    useForm<AddComponentFormValues>({
      defaultValues: EMPTY,
      mode: "onChange",
    });

  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      reset(EMPTY);
      setSubmitError(null);
    }
  }, [open, reset]);

  const siteId = watch("siteId");
  const assetId = watch("assetId");
  const assetComponentModelId = watch("assetComponentModelId");

  // Cascading turbines query — scoped to the picked Park.
  const turbinesQuery = useTurbinesListQuery({
    page: 1,
    pageSize: 200,
    sort: "internalParkName:asc",
    ...(siteId ? { site: [siteId] } : {}),
  });
  const turbineOptions = useMemo<TurbineOption[]>(() => {
    if (!siteId || !turbinesQuery.data) return [];
    return turbinesToOptions(turbinesQuery.data.items);
  }, [siteId, turbinesQuery.data]);

  // Cascading component-model query — for now we fetch ALL models (the
  // brief says the dropdown surfaces ALL component models, grouped by
  // scope; no componentGroupId narrowing per Christian's Open Q4 lock).
  const componentModelsQuery = useListComponentModelsLeanQuery();
  const componentModelOptions = useMemo(
    () => componentModelsQuery.data?.items ?? [],
    [componentModelsQuery.data?.items],
  );

  // When Park changes, clear the Turbine + Component Model picks.
  useEffect(() => {
    setValue("assetId", null, { shouldDirty: true });
    setValue("assetComponentModelId", null, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  // When Turbine changes (cleared), also clear Component Model.
  useEffect(() => {
    if (assetId === null && assetComponentModelId !== null) {
      setValue("assetComponentModelId", null, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  const canSave =
    siteId !== null &&
    assetId !== null &&
    assetComponentModelId !== null &&
    !create.isPending;

  async function onSubmit(values: AddComponentFormValues): Promise<void> {
    setSubmitError(null);
    try {
      const created = await create.mutateAsync(buildCreateBody(values));
      toast(`Component added (code ${String(created.componentCode)}).`);
      onClose();
    } catch (err) {
      const errBody = (
        err as {
          body?: { code?: string; detail?: string; reason?: string };
        }
      ).body;
      if (errBody?.detail?.startsWith("forbidden_field")) {
        toast("Cannot set this field — it's managed by the system.", "error");
      } else if (errBody?.reason === "cross_tenant_turbine") {
        // SB2-W-06 AC3 — distinct toast when the chosen turbine
        // belongs to another tenant.
        toast(
          "That turbine doesn't belong to your tenant. Pick one from your assets list.",
          "error",
        );
      } else if (errBody?.reason === "turbine_inactive") {
        toast(
          "That turbine has been deactivated. Reactivate it before adding a component.",
          "error",
        );
      } else {
        toast("Couldn't add component. Try again.", "error");
      }
      setSubmitError(errBody?.detail ?? errBody?.reason ?? "Unknown error");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="add-component-heading"
    >
      <DialogTitle id="add-component-heading">Add component</DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          {!canCreate ? (
            <Alert
              severity="info"
              variant="outlined"
              role="status"
              aria-live="polite"
              data-testid="add-modal-permission-banner"
              sx={{ mb: 2 }}
            >
              Your role does not allow adding a component.
            </Alert>
          ) : null}
          <Stack spacing={2}>
            <Controller
              name="siteId"
              control={control}
              render={({ field }) => (
                <SiteAutocomplete
                  value={field.value}
                  options={siteOptions}
                  onChange={field.onChange}
                  required
                />
              )}
            />
            <Controller
              name="assetId"
              control={control}
              render={({ field }) => (
                <TurbineAutocomplete
                  value={field.value}
                  options={turbineOptions}
                  disabled={siteId === null}
                  loading={siteId !== null && turbinesQuery.isPending}
                  onChange={field.onChange}
                  required
                />
              )}
            />
            <Controller
              name="assetComponentModelId"
              control={control}
              render={({ field }) => (
                <ComponentModelAutocomplete
                  value={field.value}
                  options={componentModelOptions}
                  disabled={assetId === null}
                  loading={assetId !== null && componentModelsQuery.isPending}
                  onChange={field.onChange}
                  required
                />
              )}
            />
            <TextField
              {...register("serialNumber")}
              label="Serial Number"
              helperText="Manufacturer serial number."
              slotProps={{
                htmlInput: {
                  "aria-label": "Serial Number",
                  maxLength: 100,
                },
              }}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("manufactureDate")}
                label="Manufacture date"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { "aria-label": "Manufacture date" },
                }}
                fullWidth
              />
              <TextField
                {...register("installedAt")}
                label="Installed at"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { "aria-label": "Installed at" },
                }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("lastMaintenanceAt")}
                label="Last maintenance at"
                type="date"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { "aria-label": "Last maintenance at" },
                }}
                fullWidth
              />
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Autocomplete<string, false, false, true>
                    freeSolo
                    options={[...STATUS_OPTIONS]}
                    value={field.value ?? ""}
                    onChange={(_e, next) => {
                      field.onChange(next ?? null);
                    }}
                    onInputChange={(_e, next) => {
                      field.onChange(next.length > 0 ? next : null);
                    }}
                    renderInput={(params) => {
                      /* eslint-disable @typescript-eslint/no-explicit-any */
                      const passThrough: any = params;
                      return (
                        <TextField
                          {...passThrough}
                          label="Status"
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              "aria-label": "Status",
                            },
                          }}
                          fullWidth
                        />
                      );
                      /* eslint-enable */
                    }}
                  />
                )}
              />
            </Stack>
            <ExternalIdsField control={control} register={register} />
            {submitError ? (
              <Box
                role="alert"
                sx={{
                  color: "error.main",
                  fontSize: 13,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,69,58,0.10)"
                      : "rgba(255,59,48,0.06)",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                {submitError}
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          {canCreate ? (
            <Button
              type="submit"
              variant="contained"
              disabled={!canSave || formState.isSubmitting}
              startIcon={
                create.isPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              Save
            </Button>
          ) : null}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
